import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Settings2, Loader2, Lock } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  useActionItemsPromptSettings,
  useUpdateActionItemsPromptSettings,
} from '../../../hooks/useActionItemsPrompt'

export function PromptSettingsDialog() {
  const { toast } = useToast()
  const { data, isLoading } = useActionItemsPromptSettings()
  const updateMutation = useUpdateActionItemsPromptSettings()

  const [open, setOpen] = useState(false)
  const [systemPrompt, setSystemPrompt] = useState('')
  const [taskInstructions, setTaskInstructions] = useState('')
  const [model, setModel] = useState('gpt-4o-mini')
  const [temperature, setTemperature] = useState(0.3)

  // Sync form state when data loads
  useEffect(() => {
    if (data?.settings) {
      setSystemPrompt(data.settings.system_prompt)
      setTaskInstructions(data.settings.task_instructions)
      setModel(data.settings.model)
      setTemperature(data.settings.temperature)
    }
  }, [data])

  const handleSave = () => {
    updateMutation.mutate(
      {
        system_prompt: systemPrompt,
        task_instructions: taskInstructions,
        model,
        temperature,
      },
      {
        onSuccess: () => {
          toast({ title: 'Settings saved', description: 'Prompt settings updated successfully.' })
          setOpen(false)
        },
        onError: (error: any) => {
          toast({
            title: 'Error',
            description: error.response?.data?.error || 'Failed to save settings',
            variant: 'destructive',
          })
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Settings2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Action Items — Prompt Settings</DialogTitle>
          <DialogDescription>
            Configure how action items are extracted from conversations.
            {data?.settings?.is_system && !data?.settings?.is_tenant_override && (
              <Badge variant="outline" className="ml-2 text-xs">System Default</Badge>
            )}
            {data?.settings?.is_tenant_override && (
              <Badge variant="secondary" className="ml-2 text-xs">Custom</Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6 py-2">
            {/* Section 1: Editable Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground">Prompt Configuration</h3>

              {/* Model */}
              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="gpt-4o-mini"
                />
                <p className="text-xs text-muted-foreground">
                  OpenAI model name (e.g. gpt-4o-mini, gpt-4o, gpt-4.1-mini)
                </p>
              </div>

              {/* Temperature */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Temperature</Label>
                  <span className="text-sm font-mono text-muted-foreground">{temperature.toFixed(1)}</span>
                </div>
                <Slider
                  value={[temperature]}
                  onValueChange={([val]) => setTemperature(val)}
                  min={0}
                  max={2}
                  step={0.1}
                />
                <p className="text-xs text-muted-foreground">
                  Lower values produce more focused output. Higher values are more creative.
                </p>
              </div>

              {/* System Prompt */}
              <div className="space-y-2">
                <Label htmlFor="system-prompt">System Prompt</Label>
                <Textarea
                  id="system-prompt"
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  rows={3}
                  placeholder="You are an expert meeting assistant..."
                />
                <p className="text-xs text-muted-foreground">
                  Sets the AI's persona and behaviour for action item extraction.
                </p>
              </div>

              {/* Task Instructions */}
              <div className="space-y-2">
                <Label htmlFor="task-instructions">Task Instructions</Label>
                <Textarea
                  id="task-instructions"
                  value={taskInstructions}
                  onChange={(e) => setTaskInstructions(e.target.value)}
                  rows={6}
                  className="font-mono text-sm"
                  placeholder="Chat transcript:\n\n{transcript}\n\nInstructions:..."
                />
                <p className="text-xs text-muted-foreground">
                  Use <code className="text-xs bg-muted px-1 rounded">{'{transcript}'}</code> as a placeholder for the conversation text.
                  This is the main instruction block sent to the AI.
                </p>
              </div>
            </div>

            <Separator />

            {/* Section 2: Read-only Output Format */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">Output Format (auto-appended)</h3>
              </div>
              <p className="text-xs text-muted-foreground">
                The following instructions are automatically appended to every request to ensure
                the AI returns structured JSON that the application can parse.
              </p>
              <div className="rounded-md border bg-muted/50 p-4">
                <pre className="text-xs font-mono whitespace-pre-wrap text-muted-foreground">
                  {data?.output_format || ''}
                </pre>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending || isLoading}
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
